import { useMutation, useQuery } from '@apollo/client/react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Typography
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import React, { useState } from 'react';
import ModalEditPaymentProvider from '../components/modals/ModalEditPaymentProvider';
import ModalKyc from '../components/modals/ModalKyc';
import { Organisation } from '../models/organisation/OrganisationModelV2';
import { Profile } from '../models/profile';
import { Kyc } from '../models/profile/Kyc';
import { OrganisationPermission } from '../models/profile/OrganisationPermission';
import { UPDATE_MY_PAYMENT_PROVIDER_CONFIG_STATUS } from '../queries/bank';
import { FetchMyProfile, GET_MY_KYC } from '../queries/profile';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setProfile, setProfileLoading, setProfileError } from '../store/profileSlice';

// GraphQL response types
interface FetchMyProfileResponse {
    result: {
        profile: Profile;
    };
}

interface KycResponse {
    kyc: {
        draft: any;
    };
}

// Main ProfilePage Component
const ProfilePage: React.FC = () => {
    const profile = useAppSelector((state) => state.profile.profile);
    const loading = useAppSelector((state) => state.profile.loading);
    const error = useAppSelector((state) => state.profile.error);
    const dispatch = useAppDispatch();
    const [isKycModalOpen, setIsKycModalOpen] = useState(false);

    const { data: kycData, refetch: refetchKyc } = useQuery<KycResponse>(GET_MY_KYC);
    const kyc = (kycData as any)?.kyc?.draft ? Kyc.build((kycData as any).kyc.draft) : undefined;

    // Refetch profile from server
    const { refetch: refetchProfile } = useQuery<FetchMyProfileResponse>(FetchMyProfile, {
        skip: true, // Don't auto-fetch, we'll trigger manually
    });

    const handleRefetchProfile = async () => {
        dispatch(setProfileLoading(true));
        try {
            const { data } = await refetchProfile();
            if (data) {
                dispatch(setProfile(Profile.build(data.result.profile)));
            }
        } catch (err: any) {
            dispatch(setProfileError(err.message || 'Failed to load profile'));
        }
    };

    const [updateStatus, { loading: updatingStatus }] = useMutation(
        UPDATE_MY_PAYMENT_PROVIDER_CONFIG_STATUS,
        {
            onCompleted: () => {
                handleRefetchProfile();
            },
            onError: (err) => {
                console.error('Error updating status:', err.message);
            },
        }
    );

    const handleConfirmStatus = () => {
        if (profile?.payment_provider_config) {
            updateStatus({
                variables: {
                    input: {
                        provider_id: profile.payment_provider_config.provider_id,
                        status: 'confirmed',
                    },
                },
            });
        }
    };

    const handleKycDismiss = (success?: boolean) => {
        setIsKycModalOpen(false);
        if (success) {
            refetchKyc();
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">Error loading profile: {error}</Alert>
            </Box>
        );
    }

    if (!profile) {
        return (
            <Box p={3}>
                <Alert severity="warning">No profile data available</Alert>
            </Box>
        );
    }

    const { person, organisations, payment_provider_config, acl } = profile;

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                Profile
            </Typography>

            {/* Personal Information */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                        <Avatar
                            sx={{ width: 80, height: 80, bgcolor: 'primary.main', fontSize: '2rem' }}
                        >
                            {person.firstname?.charAt(0).toUpperCase() || person.lastname?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {person.getFullName()}
                            </Typography>
                            {person.location && (
                                <Typography variant="body1" sx={{ mb: 0.5 }}>
                                    {person.location.address()}
                                </Typography>
                            )}
                            {person.email && (
                                <Typography variant="body1" sx={{ mb: 0.5 }}>
                                    {person.email}
                                </Typography>
                            )}
                            {person.phone_number && (
                                <Typography variant="body1">
                                    {person.phone_number}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Organizations */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Organizations
                    </Typography>
                    {organisations && organisations.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {organisations.map((org: Organisation) => (
                                <Chip
                                    key={org.id}
                                    label={org.name}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontSize: '0.9rem' }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No organizations associated
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Payment Provider Configuration */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Payment Provider
                        </Typography>
                        {payment_provider_config && (
                            <Chip
                                label={payment_provider_config.status}
                                color={
                                    payment_provider_config.status === 'verified' ? 'success' :
                                        payment_provider_config.status === 'confirmed' ? 'primary' :
                                            payment_provider_config.status === 'inreview' ? 'info' :
                                                payment_provider_config.status === 'precheck' ? 'warning' :
                                                    payment_provider_config.status === 'blocked' ? 'error' :
                                                        'default'
                                }
                                size="small"
                            />
                        )}
                    </Box>
                    {payment_provider_config ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flex: '1 1 300px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Provider ID
                                    </Typography>
                                    <Typography variant="body1">{payment_provider_config.provider_id}</Typography>
                                </Box>
                                <Box sx={{ flex: '1 1 300px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Account Name
                                    </Typography>
                                    <Typography variant="body1">{payment_provider_config.account_name}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ flex: '1 1 300px' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Account Number
                                    </Typography>
                                    <Typography variant="body1">{payment_provider_config.bank_account_number}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No payment provider configured
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <ModalEditPaymentProvider
                            currentConfig={payment_provider_config}
                            onUpdate={() => handleRefetchProfile()}
                        />
                        {payment_provider_config && payment_provider_config.status === 'verified' && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmStatus}
                        disabled={updatingStatus}
                            >
                        {updatingStatus ? <CircularProgress size={24} /> : 'Confirm Provider'}
                    </Button>
                        )}
                </Box>
            </CardContent>
        </Card>

            {/* Access Control */ }
    <Card>
        <CardContent>
            <Typography variant="h6" gutterBottom>
                Access Control
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="body2" color="text.secondary">
                            Can Niaga
                        </Typography>
                        <Chip
                            label={acl.canNiaga() ? 'Yes' : 'No'}
                            color={acl.canNiaga() ? 'success' : 'default'}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ flex: '1 1 300px' }}>
                        <Typography variant="body2" color="text.secondary">
                            Can Receive Payment
                        </Typography>
                        <Chip
                            label={acl.canReceivePayment() ? 'Yes' : 'No'}
                            color={acl.canReceivePayment() ? 'success' : 'default'}
                            size="small"
                        />
                    </Box>
                </Box>
                {acl.organisation_permissions && acl.organisation_permissions.length > 0 && (
                    <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Organization Permissions ({acl.organisation_permissions.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {acl.organisation_permissions.map((perm: OrganisationPermission) => (
                                <Chip
                                    key={perm.organisation_id}
                                    label={`Org: ${perm.organisation_id}`}
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}
            </Box>
        </CardContent>
    </Card>

    {/* KYC Verification Section */ }
            <Card>
                <CardContent>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">KYC Verification</Typography>
                            {kyc?.status && (
                                <Chip 
                                    label={kyc.status} 
                                    color={
                                        kyc.status === 'approved' ? 'success' : 
                                        kyc.status === 'REJECTED' ? 'error' : 
                                        'warning'
                                    } 
                                    size="small" 
                                />
                            )}
                        </Box>
                        {kyc?.ktpNumber && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                KTP: {kyc.ktpNumber}
                            </Typography>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<VerifiedUserIcon />}
                            onClick={() => setIsKycModalOpen(true)}
                        >
                            {kyc ? 'Update KYC' : 'Verify Identity'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <ModalKyc
                isOpen={isKycModalOpen}
                onDismiss={handleKycDismiss}
                initialData={kyc}
            />
        </Box >
    );
};

export default ProfilePage;
